/**
 * The only route in this app that is not behind a session (§11 — there is no
 * SEO surface and no public route besides this one).
 *
 * Two populations, two paths, one `person` record (§2.2). Neither path creates
 * an account: SSO matches an existing row and is refused otherwise, and a magic
 * link is only minted for an address that already exists. The page says so out
 * loud, because "ask an admin to provision you" is the correct next step for
 * someone who cannot get in, and the API — correctly — will not tell them
 * whether their address exists.
 *
 * The API redirects failures back here as `?error=<reason>&message=<text>`
 * (`apps/api/src/auth/routes.ts`), so the message rendered is the API's own.
 */

import { useNavigate, useSearch } from '@tanstack/react-router';
import { type ReactNode, type Ref, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Form, ToggleButton, ToggleButtonGroup } from 'react-aria-components';
import { ApiError } from '../api/client.ts';
import { useMe, useRequestMagicLink } from '../api/queries.ts';
import { Button } from '../components/ui/button.tsx';
import { cn, FOCUS_RING } from '../components/ui/cn.ts';
import { Alert, ErrorState } from '../components/ui/feedback.tsx';
import { TextField } from '../components/ui/field.tsx';

/** Sends the browser to the API, which owns the whole OAuth round trip. */
function startGoogleSignIn(redirect: string): void {
  const url = new URL('/auth/google', window.location.origin);
  if (redirect && redirect !== '/') url.searchParams.set('redirect', redirect);
  window.location.assign(url.toString());
}

/**
 * Google's four-color G, the mark their sign-in button is supposed to carry.
 * Untitled UI has no equivalent, and a monochrome stand-in would look like a
 * generic brand rather than Google.
 */
function GoogleMark() {
  return (
    <svg
      aria-hidden="true"
      className="size-[18px] shrink-0"
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

type Audience = 'employee' | 'client';

/** Shared with the pill so the highlight and the views settle together. */
const AUDIENCE_MOTION = 'duration-200 ease-out motion-reduce:transition-none';
/** `p-1` on the views wrapper, so a focus ring is not clipped by overflow. */
const AUDIENCE_VIEWS_PAD = 8;

/**
 * One control, two ends — the same shape as the comment-margin filter. Two
 * independent buttons would let both paths look available at once, which is
 * the layout this replaces.
 */
function AudienceToggle({
  value,
  onChange,
}: {
  value: Audience;
  onChange: (next: Audience) => void;
}) {
  return (
    <ToggleButtonGroup
      aria-label="Who is signing in"
      selectionMode="single"
      disallowEmptySelection
      selectedKeys={[value]}
      onSelectionChange={(keys) => {
        const [first] = [...keys];
        if (first === 'employee' || first === 'client') onChange(first);
      }}
      className="relative flex w-full rounded-lg bg-secondary p-0.5"
    >
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-y-0.5 left-0.5 w-[calc(50%-2px)] rounded-md bg-primary shadow-xs',
          'transition-transform',
          AUDIENCE_MOTION,
          value === 'client' && 'translate-x-full',
        )}
      />
      <AudienceButton id="employee">Employee</AudienceButton>
      <AudienceButton id="client">Client</AudienceButton>
    </ToggleButtonGroup>
  );
}

function AudienceButton({ id, children }: { id: Audience; children: string }) {
  return (
    <ToggleButton
      id={id}
      className={cn(
        'relative z-10 inline-flex h-8 flex-1 cursor-pointer select-none items-center justify-center rounded-md px-3 text-sm font-medium',
        'text-tertiary transition-colors',
        AUDIENCE_MOTION,
        'data-hovered:text-secondary data-selected:text-primary',
        FOCUS_RING,
      )}
    >
      {children}
    </ToggleButton>
  );
}

/**
 * Both views stay mounted so height can interpolate from one real size to the
 * other. Unmounting the outgoing view would snap the card; `inert` keeps the
 * hidden path out of the tab order while it is fading.
 */
function AudienceViews({
  audience,
  employee,
  client,
}: {
  audience: Audience;
  employee: ReactNode;
  client: ReactNode;
}) {
  const employeeRef = useRef<HTMLDivElement>(null);
  const clientRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();
  const [ready, setReady] = useState(false);

  useLayoutEffect(() => {
    const node = (audience === 'employee' ? employeeRef : clientRef).current;
    if (!node) return;
    const sync = () => setHeight(node.offsetHeight);
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(node);
    return () => observer.disconnect();
  }, [audience]);

  // Transition classes wait until after the first paint so the initial measure
  // does not animate the card open from nothing.
  useEffect(() => {
    if (height === undefined) return;
    setReady(true);
  }, [height]);

  return (
    <div
      className={cn(
        'relative overflow-hidden p-1',
        ready && `transition-[height] ${AUDIENCE_MOTION}`,
      )}
      style={height === undefined ? undefined : { height: height + AUDIENCE_VIEWS_PAD }}
    >
      <AudiencePanel panelRef={employeeRef} active={audience === 'employee'}>
        {employee}
      </AudiencePanel>
      <AudiencePanel panelRef={clientRef} active={audience === 'client'}>
        {client}
      </AudiencePanel>
    </div>
  );
}

function AudiencePanel({
  active,
  panelRef,
  children,
}: {
  active: boolean;
  panelRef: Ref<HTMLDivElement>;
  children: ReactNode;
}) {
  return (
    <div
      ref={panelRef}
      inert={!active}
      aria-hidden={!active}
      className={cn(
        'w-full transition-[opacity,transform]',
        AUDIENCE_MOTION,
        active
          ? 'relative translate-y-0 opacity-100'
          : 'pointer-events-none absolute inset-x-0 top-0 translate-y-1 opacity-0',
      )}
    >
      {children}
    </div>
  );
}

export function SignInPage() {
  const search = useSearch({ from: '/sign-in' });
  const navigate = useNavigate();
  const magicLink = useRequestMagicLink();
  const [email, setEmail] = useState('');
  const [audience, setAudience] = useState<Audience>('employee');

  // Someone who is already signed in and lands here (a stale bookmark, a
  // back button) belongs in the shell, not on a login form.
  const session = useMe();
  if (session.data && !search.error) {
    void navigate({ to: search.redirect ?? '/', replace: true });
  }

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <span className="grid size-11 place-items-center rounded-xl bg-brand-solid text-lg font-bold text-brand-on-solid">
            M
          </span>
          <div>
            <h1 className="text-display-xs font-semibold text-primary">Matchbox OS</h1>
            <p className="mt-1 text-sm text-tertiary">
              Administration for people, organizations, spaces, and grants.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-5 rounded-xl border border-secondary bg-primary p-6 shadow-sm">
          {search.error ? (
            <Alert tone="error" title="That sign-in did not go through">
              {search.message ?? 'Try again, or ask an administrator to check your account.'}
            </Alert>
          ) : null}

          <AudienceToggle value={audience} onChange={setAudience} />

          <AudienceViews
            audience={audience}
            employee={
              <section className="flex flex-col gap-2">
                <Button
                  variant="secondary"
                  size="lg"
                  onPress={() => startGoogleSignIn(search.redirect ?? '/')}
                >
                  <GoogleMark />
                  Continue with Google
                </Button>
                <p className="text-xs text-tertiary">
                  Restricted to the Matchbox Workspace domain. An account that has not been
                  provisioned here is refused — signing in with Google does not create one.
                </p>
              </section>
            }
            client={
              <section className="flex flex-col gap-3">
                {magicLink.isSuccess ? (
                  <Alert tone="success" title="Check your inbox">
                    {magicLink.data.message} The link is good for 15 minutes and can only be used
                    once.
                  </Alert>
                ) : (
                  <Form
                    className="flex flex-col gap-3"
                    onSubmit={(event) => {
                      event.preventDefault();
                      magicLink.mutate(email.trim());
                    }}
                  >
                    <TextField
                      label="Email address"
                      type="email"
                      value={email}
                      onChange={setEmail}
                      autoComplete="email"
                      isRequired
                      placeholder="you@company.com"
                    />
                    <Button
                      type="submit"
                      variant="secondary"
                      size="lg"
                      isPending={magicLink.isPending}
                      isDisabled={email.trim().length < 3}
                    >
                      Email me a sign-in link
                    </Button>
                  </Form>
                )}

                {/* A 429 is the one failure worth surfacing: it is the rate limit
                    in §8.6 doing its job, and "nothing happened" would be a lie. */}
                {magicLink.isError &&
                magicLink.error instanceof ApiError &&
                magicLink.error.status !== 202 ? (
                  <ErrorState error={magicLink.error} />
                ) : null}
              </section>
            }
          />
        </div>

        <p className="mt-6 text-center text-xs text-tertiary">
          Accounts are created by an administrator. If you cannot get in, ask them to check that
          your address is on file.
        </p>
      </div>
    </div>
  );
}
